// src/hooks/useRenderHook.ts

/**
 * @fileoverview Utility hooks for rendering filtered lists of items (companies, people, funds)
 * @active Used in current Next.js App Router routes
 * 
 * ⚠️ IN USE - ACTIVE COMPONENTS ⚠️
 * 
 * While these hooks are part of the legacy implementation, they are still used by
 * several active components in the application. They should be migrated to a more
 * modern implementation in the future, but for now they must be maintained.
 * 
 * @see {@link src/components/people/People.jsx} - ACTIVE component that uses this hook
 * @see {@link src/components/people/CardsList.jsx} - ACTIVE component that uses this hook
 * @see {@link src/components/funds/components/FundsMain.jsx} - ACTIVE component that uses this hook
 * @see {@link src/components/funds/components/fundsMainComponent/FundsCards.jsx} - ACTIVE component that uses this hook
 */

import { useEffect, useMemo } from "react";

import { usePathname } from "next/navigation";
import { useSelector } from "react-redux";

import { companyFieldMatching } from "@/components/companies/companiesList/dataStore/correspondingFieldToHeader";
import { useGetFilteredObjectsMutation } from "@/services/filters.service";
import contentSelectors from "@/store/content/content.selectors";
import filtersSelectors from "@/store/filters/filters.selectors";

import { useActions } from "./useActions";

interface Item {
  id: string;
  [key: string]: unknown;
}

interface SortedParams {
  direction?: 'ASC' | 'DESC';
  orderBy?: string;
}

interface GrowingFilters {
  arrayFilters?: Record<string, unknown>;
  text?: string | undefined;
}

interface StartList {
  page?: number;
  pages?: number;
  data?: unknown[];
}

interface Params {
  tag?: string;
}

export const useRenderHook = () => {
  const pathName = usePathname();
  const listForRender = useSelector(filtersSelectors.getFilteredObjects());

  const currentPage = useSelector(filtersSelectors.getFilteredCurrentPage());
  const totalPages = useSelector(filtersSelectors.getFilteredTotalPages());
  const isLoading = useSelector(contentSelectors.getIsLoading());
  const growingFilters = useSelector(contentSelectors.getGrowingFilters()) as GrowingFilters | null;
  const sortedParams = useSelector(contentSelectors.getSortedParams()) as SortedParams | null;

  const {
    setCurrentCompany,
    setCurrentFund,
    setPersonId,
    setCompanyDetailsTabIndex,
  } = useActions();

  const currentAction = () => {
    switch (pathName?.split("/")[1]) {
      case "companies":
        return setCurrentCompany;
      case "funds":
        return setCurrentFund;
      case "people":
        return setPersonId;
      default:
        return setCurrentCompany; // Default case to avoid undefined return
    }
  };

  const handleDetailsClick = (item: Item) => {
    setCompanyDetailsTabIndex(0);
    currentAction()(item);
  };

  const urlSwitcher = (pathName: string): string => {
    switch (pathName?.split("/")[1]) {
      case "companies":
        return "/res/entity/filters/companies";
      case "investors":
        return "/res/fund/filters/all";
      case "people":
        return "/res/person/filters/all";
      default:
        return "/res/entity/filters/companies"; // Default case to avoid undefined return
    }
  };

  const [getFilteredObjects] = useGetFilteredObjectsMutation();

  const getRequest = () => {
    getFilteredObjects({
      body: {
        page: currentPage + 1,
        limit: 24,
        direction: sortedParams?.direction === "ASC" ? "ASC" : "DESC",
        orderBy: sortedParams?.orderBy || "updatedAt",
        arrayFilters: growingFilters?.arrayFilters || {},
        ...(growingFilters?.text && typeof growingFilters.text === 'string' && growingFilters.text.trim()
          ? { text: growingFilters.text.trim() }
          : {}),
      },
      apiUrl: urlSwitcher(pathName),
    });
  };

  const handlerLoadMore = () => {
    if (currentPage < totalPages) {
      getRequest();
    }
  };

  const isCompany = pathName?.split("/")[1] === "companies";
  const normalizeData = useMemo(() => {
    if (isCompany && Array.isArray(listForRender)) {
      return listForRender.map((company: unknown) => companyFieldMatching(company));
    }
    return listForRender;
  }, [isCompany, listForRender]);

  return {
    pathName,
    listForRender: normalizeData,
    isResults: Array.isArray(listForRender) ? listForRender.length >= 0 : false,
    isLoading,
    isVisibleSeeMore: totalPages - currentPage > 0,
    handleDetailsClick,
    handlerLoadMore,
    getRequest,
  };
};

/**
 * Hook to initialize filtered objects data in the store
 * 
 * ⚠️ LEGACY IMPLEMENTATION - STILL IN USE BY SOME ACTIVE COMPONENTS ⚠️
 * 
 * This hook is used to initialize the filtered objects data in the Redux store.
 * It is called when a component mounts and needs to set up initial filter state.
 * 
 * @see {@link src/components/companies/Companies.tsx} - DEPRECATED component that uses this hook
 * @see {@link src/components/people/People.jsx} - ACTIVE component that uses this hook
 * @see {@link src/components/funds/components/FundsMain.jsx} - ACTIVE component that uses this hook
 */
export const useStartListHook = ({ 
  filtersFromBack, 
  startList, 
  params 
}: { 
  filtersFromBack?: Record<string, unknown>; 
  startList?: StartList; 
  params?: Params 
}) => {
  const {
    setFilteredCurrentPage,
    setFilteredTotalPages,
    setFilteredObjects,
    setGrowingFilters,
    resetFilteredObjects,
    resetGrowingFilters,
    resetSortedParams,
    setCLFiltersList,
  } = useActions();

  useEffect(() => {
    if (filtersFromBack) {
      setCLFiltersList(filtersFromBack);
    }
    if (startList) {
      resetFilteredObjects();
      resetGrowingFilters();
      resetSortedParams();

      setFilteredCurrentPage(startList?.page || 1);
      setFilteredTotalPages(startList?.pages || 1);
      setFilteredObjects(startList?.data || []);
      if (params) {
        setGrowingFilters({
          arrayFilters: {
            "Sector / Industry Tags": [params?.tag],
          },
          text: "", // Empty string is compatible with the GrowingFilters interface
        } as GrowingFilters);
      }
    }
  }, [
    filtersFromBack,
    startList,
    params,
    setFilteredObjects,
    setGrowingFilters,
    resetFilteredObjects,
    resetGrowingFilters,
    resetSortedParams,
    setFilteredCurrentPage,
    setFilteredTotalPages,
    setCLFiltersList,
  ]);
};
